import { DisclosureSection } from '@components/core-ui/DisclosureSection';
import { Header } from '@components/Header';
import AddIcon from '@components/icons/Add.svg';
import { LinearProgressLoader } from '@components/core-ui/spinners/LinearProgressLoader';
import { TeamCards } from '@components/TeamCards';
import { ModalNamesEnum } from 'app/types/enums/ModalNamesEnum';
import { useAPI } from '@providers/useAPI';
import { useAuthContext } from '@providers/AuthProvider';
import { useModalContext } from '@providers/ModalProvider';
import { Restricted } from '@providers/PermissionProvider/Restricted';
import { routes } from '@routes';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { OrganizationTeamWithMembersResponseDto, WorkspaceSubscriptionsService, WorkspacesService } from 'app/API';
import { workspacesKeys } from 'app/queries/workspaces/workspacesKeys';
import { EPermission } from 'app/types/enums/EPermission';
import { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import toast from 'react-hot-toast';
import { useNavigate, useParams } from 'react-router';

import { createFetchWorkspaceQueryFn } from './api/fetchWorkspace';
import { createFetchWorkspaceTeamsQueryFn } from 'app/queries/workspaces/createFetchWorkspaceTeamsQueryFn';
import { WorkspaceDetailsForm } from './components/Form/form';
import EyeIcon from '@components/icons/FormEyeOpen.svg';
import EyeClosedIcon from '@components/icons/FormEyeClosed.svg';
import { OutlinedButton } from '@components/core-ui/form-elements/buttons/Button/variations/OutlinedButton';
import { TextButton } from '@components/core-ui/form-elements/buttons/Button/variations/TextButton';
import { ToolbarActionButton } from '@components/core-ui/form-elements/buttons/Button/variations/ToolbarActionButton';
import { Wrapper } from './styles/Wrapper';

const EMPTY_TEAMS: OrganizationTeamWithMembersResponseDto[] = [];

export const WorkspaceDetailsPage = () => {
  const { call } = useAPI();
  const queryClient = useQueryClient();
  const { orgSlug = '', wsSlug = '' } = useParams<{ orgSlug: string; wsSlug: string }>();

  const { organization, me } = useAuthContext();
  const [teamsDisclosureOpen, setTeamsDisclosureOpen] = useState<boolean>(true);
  const { openModal, closeModal } = useModalContext();
  const navigate = useNavigate();

  const workspaceKey = workspacesKeys.detailBySlugs(orgSlug, wsSlug);
  const teamsKey = workspacesKeys.teamsBySlugs(orgSlug, wsSlug);

  const workspaceQuery = useQuery({
    queryKey: workspaceKey,
    queryFn: createFetchWorkspaceQueryFn(call),
    enabled: orgSlug !== '' && wsSlug !== '',
  });
  const workspace = workspaceQuery.data;

  const teamsQuery = useQuery({
    queryKey: teamsKey,
    queryFn: createFetchWorkspaceTeamsQueryFn(call),
    enabled: orgSlug !== '' && wsSlug !== '',
  });
  const teams = teamsQuery.data?.result ?? EMPTY_TEAMS;

  const refreshWorkspaceAndTeams = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: workspaceKey }),
      queryClient.invalidateQueries({ queryKey: teamsKey }),
    ]);

  const refreshWorkspace = () => queryClient.invalidateQueries({ queryKey: workspaceKey });

  const removeTeamMutation = useMutation({
    mutationFn: (teamId: string) =>
      call(() => WorkspacesService.updateWorkspaceByRemovingTeam({ orgSlug, wsSlug, teamId }), {
        onError: (message) => toast.error(message || 'Unable to remove the team.'),
      }),
    onSettled: refreshWorkspaceAndTeams,
  });

  const addTeamsMutation = useMutation({
    mutationFn: (teamIds: string[]) =>
      call(() => WorkspacesService.updateWorkspaceByAddingTeam({ orgSlug, wsSlug, requestBody: { teamIds } }), {
        onError: (message) => toast.error(message || 'Unable to add the team.'),
      }),
    onSettled: refreshWorkspaceAndTeams,
  });

  const watchMutation = useMutation({
    mutationFn: (workspaceId: string) =>
      call(() => WorkspaceSubscriptionsService.create({ workspaceId }), {
        onError: (message) => toast.error(message || 'Unable to watch this workspace.'),
      }),
    onSettled: refreshWorkspace,
  });

  const unwatchMutation = useMutation({
    mutationFn: (workspaceId: string) =>
      call(() => WorkspaceSubscriptionsService.delete({ workspaceId }), {
        onError: (message) => toast.error(message || 'Unable to unwatch this workspace.'),
      }),
    onSettled: refreshWorkspace,
  });

  const isWatchPending = watchMutation.isPending || unwatchMutation.isPending;
  const isTeamsPending = removeTeamMutation.isPending || addTeamsMutation.isPending;
  const isBusy = workspaceQuery.isFetching || teamsQuery.isFetching || isWatchPending || isTeamsPending;

  const handleWorkspaceSaved = (savedSlug: string) => {
    if (savedSlug !== wsSlug) {
      queryClient.setQueryData(workspacesKeys.detailBySlugs(orgSlug, savedSlug), workspaceQuery.data);
      queryClient.setQueryData(workspacesKeys.teamsBySlugs(orgSlug, savedSlug), teamsQuery.data);
    }
    void queryClient.invalidateQueries({ queryKey: workspacesKeys.detailBySlugs(orgSlug, savedSlug) });
    navigate(routes.workspaceDetails.make(organization?.slug, savedSlug));
  };

  const handleRemoval = (teamId: string) => {
    removeTeamMutation.mutate(teamId);
  };

  const handleAddTeam = () => {
    openModal(ModalNamesEnum.AddTeamToWorkspaceModal, {
      orgSlug,
      workspaceSlug: wsSlug,
      onSuccess: (selectedTeams: string[]) => {
        closeModal();
        addTeamsMutation.mutate(selectedTeams);
      },
      onCancel: () => {
        closeModal();
      },
    });
  };

  const headerLeftSideContent = [
    <OutlinedButton key="button" className="w-[100px] gap-[5px]" onClick={handleAddTeam}>
      <AddIcon height="12px" width="12px" />
      Add team
    </OutlinedButton>,
  ];

  const renderWatchButton = () => {
    if (!workspace) {
      return null;
    }
    if (workspace.watchers?.includes(me.id)) {
      return (
        <ToolbarActionButton
          title="Unwatch this workspace"
          disabled={isWatchPending}
          onClick={() => unwatchMutation.mutate(workspace.id)}
        >
          <EyeClosedIcon style={{ marginRight: '-5px', height: '28px', width: '28px' }} />
          Unwatch
        </ToolbarActionButton>
      );
    }
    return (
      <ToolbarActionButton
        title="Watch this workspace"
        disabled={isWatchPending}
        onClick={() => watchMutation.mutate(workspace.id)}
      >
        <EyeIcon style={{ marginRight: '-5px', height: '28px', width: '28px' }} />
        Watch
      </ToolbarActionButton>
    );
  };

  const teamsTitle = `Teams${teams.length > 0 ? ` (${teams.length})` : ''}`;

  return (
    <>
      <Helmet>
        <title>{workspace?.name || 'Workspace'} - AutoScript</title>
      </Helmet>
      <Header title={workspace?.name} leftSideContent={headerLeftSideContent} />
      <LinearProgressLoader active={isBusy} />

      {workspaceQuery.isError ? (
        <div className="m-4 flex items-center gap-3 text-xs text-[var(--color-text-muted)]" role="alert">
          <span>Failed to load workspace.</span>
          <TextButton type="button" onClick={() => void workspaceQuery.refetch()}>
            Retry
          </TextButton>
        </div>
      ) : null}

      {workspace ? (
        <Wrapper>
          <div className="head-buttons">
            {organization?.permissions?.manageOrganization ? renderWatchButton() : null}
          </div>
          <WorkspaceDetailsForm key={workspace.id} workspace={workspace} onSaved={handleWorkspaceSaved} />

          <div>
            <Restricted to={EPermission.manageMembers}>
              <DisclosureSection
                title={teamsTitle}
                open={teamsDisclosureOpen}
                setOpenState={setTeamsDisclosureOpen}
                loading={false}
              >
                {teamsQuery.isError ? (
                  <div className="mt-4 flex items-center gap-3 text-xs text-[var(--color-text-muted)]" role="alert">
                    <span>Failed to load teams.</span>
                    <TextButton type="button" onClick={() => void teamsQuery.refetch()}>
                      Retry
                    </TextButton>
                  </div>
                ) : null}
                <TeamCards teams={teams} handleRemoval={handleRemoval} addTeam={handleAddTeam} />
              </DisclosureSection>
            </Restricted>
          </div>
        </Wrapper>
      ) : null}
    </>
  );
};
